/**
 * Un desplegable que además deja **crear la opción que falta**, sin salir de
 * donde se está.
 *
 * El caso que lo motiva: registrar una entidad cuyo tipo no está en la lista.
 * Antes había que abandonar el formulario a medio llenar, ir a otra pantalla
 * —que ni siquiera existía, porque los tipos estaban en un `CHECK` de la base—
 * y volver a empezar. Es la interrupción clásica de los catálogos cerrados.
 *
 * `UI-5`: no se reimplementa nada. El desplegable es el `Select` del design
 * system y el alta son su `Input` y su `Button`; lo único propio es el estado
 * que alterna entre los dos y el ítem centinela que dispara el cambio.
 *
 * Es genérico a propósito —recibe `onCrear` y no sabe qué está creando—, para
 * que el siguiente catálogo que crezca no vuelva a escribir esto.
 */
import { useState } from 'react'
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mpfn/ui'
import { Check, Plus, X } from 'lucide-react'
import type { Opcion } from '../api/catalogos.js'

/**
 * Valor del ítem que abre el alta.
 *
 * Lleva caracteres que un código real no puede tener —`codigoDesdeEtiqueta`
 * del servidor solo produce `[a-z0-9_]`—, así que no puede chocar con una
 * opción legítima por mucho que crezca el catálogo.
 */
const CENTINELA = '::crear::'

interface Props {
  id: string
  opciones: readonly Opcion[]
  valor: string
  onCambio: (codigo: string) => void
  /** Texto del desplegable cuando no hay nada elegido. */
  marcador: string
  /** Lo que se lee en el ítem de alta: «Crear un tipo nuevo…». */
  etiquetaAlta: string
  /** Rótulo del campo de texto mientras se escribe el nombre nuevo. */
  etiquetaCampo: string
  /**
   * Da de alta y devuelve la opción creada. Quien lo implementa decide si eso
   * toca la red; aquí solo se espera y se selecciona el resultado.
   */
  onCrear: (etiqueta: string) => Promise<Opcion>
}

export function SelectorConAlta({
  id,
  opciones,
  valor,
  onCambio,
  marcador,
  etiquetaAlta,
  etiquetaCampo,
  onCrear,
}: Props) {
  const [creando, setCreando] = useState(false)
  const [borrador, setBorrador] = useState('')
  const [enCurso, setEnCurso] = useState(false)

  const cerrarAlta = () => {
    setCreando(false)
    setBorrador('')
  }

  const confirmar = () => {
    if (borrador.trim() === '' || enCurso) return
    setEnCurso(true)
    onCrear(borrador.trim())
      .then((creada) => {
        // Se deja elegida: crearla y no seleccionarla obligaría a volver al
        // desplegable a buscar lo que se acaba de escribir.
        onCambio(creada.codigo)
        cerrarAlta()
      })
      .finally(() => {
        setEnCurso(false)
      })
  }

  if (creando) {
    return (
      <div className="sigfisc-alta-en-linea">
        <Input
          id={id}
          value={borrador}
          autoFocus
          aria-label={etiquetaCampo}
          placeholder={etiquetaCampo}
          disabled={enCurso}
          onChange={(evento) => {
            setBorrador(evento.target.value)
          }}
          // Intro confirma y Escape vuelve. Sin esto había que ir al botón con
          // el ratón, que en un formulario que se está tecleando es un frenazo.
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') {
              // El alta vive dentro de un `<form>`: sin esto, Intro lo enviaría.
              evento.preventDefault()
              confirmar()
            }
            if (evento.key === 'Escape') {
              evento.preventDefault()
              cerrarAlta()
            }
          }}
        />
        <Button
          variant="primary"
          size="sm"
          type="button"
          loading={enCurso}
          disabled={borrador.trim() === ''}
          onClick={confirmar}
        >
          <Check size={14} aria-hidden="true" />
          Crear
        </Button>
        <Button variant="ghost" size="sm" type="button" disabled={enCurso} onClick={cerrarAlta}>
          <X size={14} aria-hidden="true" />
          <span className="mpfn-sr-only">Cancelar el alta</span>
        </Button>
      </div>
    )
  }

  return (
    <Select
      value={valor}
      onValueChange={(elegido) => {
        if (elegido === CENTINELA) {
          setCreando(true)
          return
        }
        onCambio(elegido)
      }}
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder={marcador} />
      </SelectTrigger>
      <SelectContent>
        {opciones.map((opcion) => (
          <SelectItem key={opcion.codigo} value={opcion.codigo}>
            {opcion.etiqueta}
          </SelectItem>
        ))}
        {/* Al final y no al principio: es la salida de emergencia, no lo que
            se busca normalmente. */}
        <SelectItem value={CENTINELA}>
          <span className="sigfisc-alta-en-linea__disparador">
            <Plus size={14} aria-hidden="true" />
            {etiquetaAlta}
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  )
}
